import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import * as z from "zod/v4";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { ArrowLeft, CheckCircle } from "lucide-react";
import ky from "ky";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { generateDefaultValue, requiredString } from "@/lib/utils/zod";
import { env } from "@/env";

const forgotPasswordSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/auth/forgot-password")({
  component: RouteComponent,
  validateSearch: zodValidator(forgotPasswordSearchSchema),
  staticData: {
    title: "Password Recovery",
  },
});

const ForgotPasswordSchema = z.object({
  email: requiredString("Email is required"),
});
type ForgotPasswordSchema = z.infer<typeof ForgotPasswordSchema>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm<ForgotPasswordSchema>({
    resolver: standardSchemaResolver(ForgotPasswordSchema),
    defaultValues: generateDefaultValue(ForgotPasswordSchema),
  });

  const forgotPassword = useMutation({
    async mutationFn({ email }: ForgotPasswordSchema) {
      await ky.post("api/v1/auth/forgot-password", {
        baseUrl: env.VITE_AUTH_API_URL,
        json: { email },
      });
    },
    onSuccess() {
      setEmailSent(true);
    },
  });

  if (emailSent) {
    return (
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
        <div className="w-full space-y-6">
          <div className="space-y-4 text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h1 className="text-2xl font-bold">Check Your Email</h1>
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              We've sent a password reset link to your email address. Please
              check your inbox and click the link to reset your password.
            </p>
          </div>

          <Link
            className={cn(
              buttonVariants({ variant: "outline", size: "lg" }),
              "flex h-12 w-full items-center justify-center gap-2 text-sm",
            )}
            to="/auth/login"
            search={{ redirect: searchParams.redirect }}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Log in</span>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
      <div className="w-full space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold">Forgot Password?</h1>
          <p className="text-sm text-gray-500 dark:text-muted-foreground">
            Enter your email so we can send you a password reset link.
          </p>
        </div>

        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit((formData) => {
              if (forgotPassword.isPending) return;
              forgotPassword.mutate(formData);
            })}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black dark:text-white">
                    * Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      placeholder="Email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <button
              type="submit"
              disabled={forgotPassword.isPending}
              className="w-full rounded-md bg-[#dd2590] py-3 font-medium text-white transition-colors hover:bg-pink-700 disabled:opacity-60"
            >
              {forgotPassword.isPending ? "Sending..." : "Send Email"}
            </button>

            <Link
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "flex h-12 w-full items-center justify-center gap-2 text-sm",
              )}
              to="/auth/login"
              search={{ redirect: searchParams.redirect }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Log in</span>
            </Link>

            {form.formState.errors.root?.serverError && (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            )}
          </form>
        </Form>
      </div>
    </main>
  );
}
